import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { AlertTriangle, HeartPulse, Ban } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { articleSchema, breadcrumbSchema, faqPageSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

const sideEffectsFaqs: Record<"de" | "en", { q: string; a: string }[]> = {
  de: [
    { q: "Welche Nebenwirkungen hat Retatrutid am häufigsten?", a: "Am häufigsten sind gastrointestinale Nebenwirkungen: Übelkeit (bis 45 %), Erbrechen (10–20 %), Durchfall (15–25 %) und Verstopfung (10–15 %). Sie sind dosisabhängig, meist mild bis moderat und treten vor allem in den ersten 4–8 Wochen auf." },
    { q: "Beeinflusst Retatrutid Herzfrequenz oder Blutdruck?", a: "In Phase-II-Daten wurde ein leichter Anstieg der Ruhe-Herzfrequenz um durchschnittlich 5–7 Schläge pro Minute beobachtet – vergleichbar mit Semaglutid und Tirzepatid. Der Blutdruck sank in den meisten Studien leicht. Langzeitdaten zu kardiovaskulären Endpunkten stehen noch aus." },
    { q: "Kann Retatrutid Pankreatitis oder Gallensteine auslösen?", a: "Wie andere Inkretin-Mimetika kann Retatrutid theoretisch Pankreatitis oder Gallensteine begünstigen. Diese seltenen, aber relevanten Risiken werden im TRIUMPH-Phase-III-Programm systematisch erfasst." },
    { q: "Gibt es ein Risiko für Schilddrüsenkrebs?", a: "Aus Tierstudien mit anderen GLP-1-Wirkstoffen wird ein erhöhtes Risiko für medulläres Schilddrüsenkarzinom extrapoliert, insbesondere bei familiärer Vorbelastung. Retatrutid ist daher bei entsprechender Vorgeschichte oder MEN2 kontraindiziert." },
    { q: "Wann ist Retatrutid kontraindiziert?", a: "Voraussichtliche Kontraindikationen: persönliche oder familiäre Vorgeschichte von medullärem Schilddrüsenkarzinom, multiple endokrine Neoplasie Typ 2 (MEN2), Schwangerschaft und Stillzeit sowie akute Pankreatitis in der Anamnese." },
  ],
  en: [
    { q: "What are the most common side effects of Retatrutide?", a: "The most common are gastrointestinal: nausea (up to 45%), vomiting (10–20%), diarrhea (15–25%) and constipation (10–15%). They are dose-dependent, usually mild to moderate and appear mostly during the first 4–8 weeks." },
    { q: "Does Retatrutide affect heart rate or blood pressure?", a: "Phase II data showed a mild increase in resting heart rate averaging 5–7 beats per minute — similar to Semaglutide and Tirzepatide. Blood pressure decreased slightly in most trials. Long-term cardiovascular outcome data are still pending." },
    { q: "Can Retatrutide trigger pancreatitis or gallstones?", a: "Like other incretin mimetics, Retatrutide may theoretically raise the risk of pancreatitis or gallstones. These rare but relevant endpoints are systematically captured in the TRIUMPH Phase III program." },
    { q: "Is there a thyroid cancer risk?", a: "Animal studies with other GLP-1 compounds suggest an increased risk of medullary thyroid carcinoma, particularly with a corresponding family history. Retatrutide is therefore contraindicated in patients with that history or MEN2." },
    { q: "When is Retatrutide contraindicated?", a: "Expected contraindications include a personal or family history of medullary thyroid carcinoma, multiple endocrine neoplasia type 2 (MEN2), pregnancy and breastfeeding, and a history of acute pancreatitis." },
  ],
};

export const Route = createFileRoute("/nebenwirkungen")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const lang = loaderData?.lang ?? "en";
    return {
      ...localizedHead("/nebenwirkungen", loaderData?.lang),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/nebenwirkungen", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/nebenwirkungen", loaderData?.lang, { specialty: "Pharmacologic", aspect: "SideEffects" })) },
        { type: "application/ld+json", children: JSON.stringify(faqPageSchema("/nebenwirkungen", loaderData?.lang, sideEffectsFaqs[lang])) },
        { type: "application/ld+json", children: JSON.stringify(articleSchema("/nebenwirkungen", loaderData?.lang, { section: "Side effects" })) },
      ],
    };
  },
  component: Page,
});



function Page() {
  const { lang } = useI18n();
  const de = lang === "de";

  const c = de
    ? {
        eyebrow: "Sicherheitsprofil",
        h1: "Nebenwirkungen von Retatrutid",
        lead: "Retatrutid zeigt in Studien ein Nebenwirkungsprofil, das den etablierten GLP-1-Wirkstoffen ähnelt. Die häufigsten unerwünschten Wirkungen sind gastrointestinal und dosisabhängig – die meisten treten in den ersten Wochen der Therapie auf.",
        gi: {
          title: "Gastrointestinale Nebenwirkungen (häufig)",
          items: [
            { name: "Übelkeit", freq: "bis zu 45 %", body: "Häufigste Nebenwirkung, v. a. bei Dosis-Steigerung. Meist mild bis moderat." },
            { name: "Erbrechen", freq: "10–20 %", body: "Tritt vor allem in den ersten 4–8 Wochen auf, klingt bei den meisten Betroffenen ab." },
            { name: "Durchfall", freq: "15–25 %", body: "Dosisabhängig, in der Regel selbstlimitierend." },
            { name: "Verstopfung", freq: "10–15 %", body: "Gegenspieler zum Durchfall, häufig durch verzögerte Magenentleerung." },
            { name: "Appetitverlust", freq: "erwartet", body: "Kein Nebeneffekt im engeren Sinn – Teil der beabsichtigten Wirkung." },
          ],
        },
        cv: {
          title: "Herz-Kreislauf-Effekte",
          body: "In den Phase-II-Daten wurde ein leichter Anstieg der Ruhe-Herzfrequenz um durchschnittlich 5–7 Schläge pro Minute beobachtet – ähnlich wie bei Semaglutid und Tirzepatid. Blutdruck sank in den meisten Studien leicht. Langzeitdaten zu kardiovaskulären Endpunkten stehen noch aus.",
        },
        rare: {
          title: "Seltene, aber relevante Risiken",
          body: "Wie andere Inkretin-Mimetika kann Retatrutid theoretisch Pankreatitis, Gallensteine oder – bei entsprechender familiärer Vorbelastung – ein erhöhtes Risiko für medulläres Schilddrüsenkarzinom (aus Tierstudien mit anderen GLP-1-Wirkstoffen extrapoliert) begünstigen. Diese Endpunkte werden im Phase-III-Programm systematisch erfasst.",
        },
        ci: {
          title: "Kontraindikationen (voraussichtlich)",
          items: [
            "Persönliche oder familiäre Vorgeschichte von medullärem Schilddrüsenkarzinom",
            "Multiple endokrine Neoplasie Typ 2 (MEN2)",
            "Schwangerschaft und Stillzeit",
            "Akute Pankreatitis in der Anamnese",
          ],
        },
        note: "Wichtig:",
        noteBody:
          "Alle Angaben stammen aus veröffentlichten Phase-I- und Phase-II-Daten. Das finale Nebenwirkungsprofil ergibt sich erst nach Abschluss des TRIUMPH-Programms und der Zulassung. Diese Seite ersetzt keine ärztliche Beratung.",
      }
    : {
        eyebrow: "Safety profile",
        h1: "Side effects of Retatrutide",
        lead: "In trials, Retatrutide shows a side-effect profile similar to established GLP-1 compounds. The most common adverse effects are gastrointestinal and dose-dependent — most appear during the first weeks of therapy.",
        gi: {
          title: "Gastrointestinal side effects (common)",
          items: [
            { name: "Nausea", freq: "up to 45%", body: "Most common effect, especially during dose escalation. Usually mild to moderate." },
            { name: "Vomiting", freq: "10–20%", body: "Occurs mainly in the first 4–8 weeks, resolves in most patients." },
            { name: "Diarrhea", freq: "15–25%", body: "Dose-dependent, typically self-limiting." },
            { name: "Constipation", freq: "10–15%", body: "Counterpart to diarrhea, often driven by delayed gastric emptying." },
            { name: "Loss of appetite", freq: "expected", body: "Not really an adverse effect — part of the intended action." },
          ],
        },
        cv: {
          title: "Cardiovascular effects",
          body: "Phase II data showed a mild increase in resting heart rate averaging 5–7 beats per minute — similar to Semaglutide and Tirzepatide. Blood pressure decreased slightly in most trials. Long-term cardiovascular outcome data are still pending.",
        },
        rare: {
          title: "Rare but relevant risks",
          body: "Like other incretin mimetics, Retatrutide may theoretically increase risk of pancreatitis, gallstones or — with a corresponding family history — medullary thyroid carcinoma (extrapolated from animal studies with other GLP-1 compounds). These endpoints are systematically captured in the Phase III program.",
        },
        ci: {
          title: "Contraindications (expected)",
          items: [
            "Personal or family history of medullary thyroid carcinoma",
            "Multiple endocrine neoplasia type 2 (MEN2)",
            "Pregnancy and breastfeeding",
            "History of acute pancreatitis",
          ],
        },
        note: "Important:",
        noteBody:
          "All figures come from published Phase I and Phase II data. The final side-effect profile will only be known after completion of the TRIUMPH program and approval. This page does not replace medical advice.",
      };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">{c.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{c.lead}</p>
      </div>

      <section className="mt-12">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          {c.gi.title}
        </h2>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
          {c.gi.items.map((it, i) => (
            <motion.div
              key={it.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
              className={`grid gap-2 p-5 md:grid-cols-[1fr_auto_2fr] md:items-center ${
                i > 0 ? "border-t border-border" : ""
              }`}
            >
              <div className="font-semibold text-foreground">{it.name}</div>
              <div className="font-mono text-xs uppercase tracking-wider text-indigo">{it.freq}</div>
              <div className="text-sm text-muted-foreground">{it.body}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <HeartPulse className="h-5 w-5 text-indigo" />
          {c.cv.title}
        </h2>
        <p className="mt-3 text-muted-foreground">{c.cv.body}</p>
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          {c.rare.title}
        </h2>
        <p className="mt-3 text-muted-foreground">{c.rare.body}</p>
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <Ban className="h-5 w-5 text-rose-500" />
          {c.ci.title}
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {c.ci.items.map((x) => (
            <li key={x} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-rose-500" />
              {x}
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-10 text-xs text-muted-foreground">
        <strong className="text-foreground">{c.note}</strong> {c.noteBody}
      </p>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/wissenschaft", "/studien", "/sicherheit"]} />
    </div>
  );
}
